import { Box, Space, SpaceVertical } from '@looker/components';
import { BIIconButton, BISelect, globalTheme } from '@pulsepointinc/bi-components';
import type { DsInputCustomEvent } from '@pulsepointinc/design-system';
import { DsCheckbox, DsInput, DsTypography } from '@pulsepointinc/design-system-react';
import { isEqual } from 'lodash';
import { type FC, useCallback, useId, useMemo, useState } from 'react';
import type { Filter } from '../../../../../../stores/filters';
import { useFiltersStore } from '../../../../../../stores/filters';
import { AppliedText, EditButton } from './styles';

interface FrequencyProps {
  onChange: (filter: Filter) => void;
  filterName: string;
  label: string;
  subHeading?: string;
  suffix?: string;
  showOperatorSelect?: boolean;
}

type ComparisonOperator = '<' | '<=' | '=' | '>=' | '>';

const COMPARISON_OPERATORS: Array<{ value: ComparisonOperator; label: string }> = [
  { value: '<', label: '<' },
  { value: '<=', label: '<=' },
  { value: '=', label: '=' },
  { value: '>=', label: '>=' },
  { value: '>', label: '>' },
];

export const Frequency: FC<FrequencyProps> = ({
  onChange,
  filterName,
  label,
  subHeading,
  suffix,
  showOperatorSelect = false,
}) => {
  const frequencyCheckboxId = useId();

  const initialState = useMemo(() => {
    const state = useFiltersStore.getState();
    const filterState = state.filters[filterName];
    const draftState = state.draftFilters[filterName];

    const isDirty = draftState !== undefined && !isEqual(draftState, filterState);
    const filterValue = isDirty ? draftState : filterState;

    const rawExpression = filterValue?.expression ?? '';
    let parsedOperator: ComparisonOperator = '<';
    let parsedInputValue: string | number = '';

    if (rawExpression) {
      if (showOperatorSelect) {
        const operatorMatch = rawExpression.match(/^(<=|>=|<|>|=)?(.*)$/);
        if (operatorMatch) {
          parsedOperator = (operatorMatch[1] || '=') as ComparisonOperator;
          const valStr = operatorMatch[2];
          parsedInputValue = valStr === '' ? '' : parseInt(valStr, 10);
        }
      } else {
        parsedInputValue = parseInt(rawExpression, 10);
      }
    }

    const filterEnabled = !!filterValue?.enabled && (showOperatorSelect || !!rawExpression);

    return {
      inputValue: parsedInputValue,
      operator: parsedOperator,
      isEnabled: filterEnabled,
      isEditMode: isDirty || !rawExpression,
    };
  }, [filterName, showOperatorSelect]);

  const [inputValue, setInputValue] = useState(initialState.inputValue);
  const [isFrequencyControlEnabled, setIsFrequencyControlEnabled] = useState(initialState.isEnabled);
  const [selectedOperator, setSelectedOperator] = useState(initialState.operator);
  const [isEditMode, setIsEditMode] = useState(initialState.isEditMode);

  const buildExpression = useCallback(
    (operator: ComparisonOperator, value: string | number): string => {
      if (value === '') {
        return showOperatorSelect ? operator : '';
      }
      return operator === '=' ? value.toString() : `${operator}${value}`;
    },
    [showOperatorSelect],
  );

  const updateStore = useCallback(
    (filter: Filter) => {
      if (!showOperatorSelect) {
        onChange(filter);
      } else {
        useFiltersStore.getState().setDraftFilter(filter);
      }
    },
    [showOperatorSelect, onChange],
  );

  const handleInputChange = (value: string | number) => {
    if (typeof value !== 'string' || !/^\d*$/.test(value)) {
      return;
    }

    const numericValue = value === '' ? '' : Number(value);
    setInputValue(value === '' ? '' : numericValue);

    if (isFrequencyControlEnabled) {
      const expression = showOperatorSelect ? buildExpression(selectedOperator, numericValue) : numericValue.toString();
      const filter: Filter = {
        filterName,
        expression,
        enabled: true,
        expressionType: 'target',
      };
      updateStore(filter);
    }
  };

  const handleOperatorChange = (newValue: string | string[]): void => {
    if (typeof newValue !== 'string') return;

    const operator = newValue as ComparisonOperator;
    setSelectedOperator(operator);

    if (isFrequencyControlEnabled) {
      const expression = showOperatorSelect ? buildExpression(operator, inputValue) : inputValue.toString();
      const filter: Filter = {
        filterName,
        expression: expression ?? '',
        enabled: true,
        expressionType: 'target',
      };
      updateStore(filter);
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    setIsFrequencyControlEnabled(checked);

    const filter: Filter = {
      filterName,
      expression: checked ? buildExpression(selectedOperator, inputValue) : '',
      enabled: checked,
      expressionType: 'target',
    };

    if (!checked) {
      onChange(filter);
      useFiltersStore.getState().clearDraftFilter(filterName);
      setInputValue('');
    } else {
      updateStore(filter);
      setIsEditMode(true);
    }
  };

  const handleApply = () => {
    if (!isFrequencyControlEnabled) return;

    const filter: Filter = {
      filterName,
      expression: buildExpression(selectedOperator, inputValue),
      enabled: true,
      expressionType: 'target',
    };

    onChange(filter);
    setIsEditMode(false);
    useFiltersStore.getState().clearDraftFilter(filterName);
  };

  const handleEdit = () => setIsEditMode(true);

  const handleClear = () => {
    setInputValue('');
    setSelectedOperator(COMPARISON_OPERATORS[0].value);
    handleCheckboxChange(false);
  };

  const appliedText =
    inputValue !== undefined && inputValue !== ''
      ? `${selectedOperator === '=' ? '' : selectedOperator} ${inputValue} ${suffix || ''}`.trim()
      : '';

  return (
    <SpaceVertical gap="u1">
      <Space gap="u2" align="center">
        <DsCheckbox
          id={frequencyCheckboxId}
          onCheckedChange={(event: CustomEvent<boolean>) => handleCheckboxChange(event.detail)}
          checked={isFrequencyControlEnabled}
        >
          <DsTypography as="span" variant={showOperatorSelect ? 'body' : 'label'}>
            {showOperatorSelect ? `Enable ${label}` : label}
          </DsTypography>
        </DsCheckbox>
      </Space>

      {showOperatorSelect && isFrequencyControlEnabled && !isEditMode && appliedText && (
        <SpaceVertical gap="u1" paddingLeft="u8">
          <AppliedText color="secondary">{appliedText}</AppliedText>
          <EditButton color="invisible" onClick={handleEdit}>
            Edit {label}
          </EditButton>
        </SpaceVertical>
      )}

      {isFrequencyControlEnabled && (isEditMode || !showOperatorSelect) && (
        <Box
          width="100%"
          padding={showOperatorSelect ? 'u4' : undefined}
          paddingLeft={showOperatorSelect ? undefined : 'u8'}
          backgroundColor={showOperatorSelect ? globalTheme.colors.backgroundLight : undefined}
          borderRadius={showOperatorSelect ? '6px' : undefined}
        >
          <SpaceVertical gap="u3">
            <DsTypography color="secondary">{subHeading}</DsTypography>

            <Space gap="u2" align="center" width="auto">
              {showOperatorSelect && (
                <Box width={'80px'}>
                  <BISelect options={COMPARISON_OPERATORS} onChange={handleOperatorChange} value={selectedOperator} />
                </Box>
              )}
              <Box width={showOperatorSelect ? undefined : '80px'} flex={showOperatorSelect ? 1 : undefined}>
                <DsInput
                  type="text"
                  onValueChange={(e: DsInputCustomEvent<string>) => handleInputChange(e.detail)}
                  value={String(inputValue)}
                />
              </Box>
              <DsTypography>{suffix}</DsTypography>
            </Space>

            {showOperatorSelect && (
              <Space gap="u3" marginTop="u4" width="100%" color={globalTheme.colors.textPrimary} justify="end">
                <BIIconButton icon="Clear" label="Clear" onClick={handleClear} />
                <BIIconButton
                  icon="CheckCircle"
                  label="Apply"
                  disabled={inputValue === undefined || inputValue === ''}
                  onClick={handleApply}
                />
              </Space>
            )}
          </SpaceVertical>
        </Box>
      )}
    </SpaceVertical>
  );
};
